import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { CrumbProvider, useCrumbState } from "@/context/CrumbContext";
import InstallAppButton from "@/components/InstallAppButton";
import ProfileModal from "@/components/ProfileModal";
import {
  Archive, BarChart3, ChevronRight, ClipboardCheck, Flame, Home, LogOut, MapPinned, Menu, PenLine, ScrollText, X,
} from "lucide-react";

export const NAV = [
  { to: "/home", label: "Ana Sayfa", icon: Home, id: "home" },
  { to: "/assessments", label: "Değerlendirmeler", icon: ClipboardCheck, id: "assessments" },
  { to: "/reports", label: "Rapor Arşivi", icon: Archive, id: "reports" },
  { to: "/map", label: "Türkiye Haritası", icon: MapPinned, id: "map" },
  { to: "/analytics", label: "AI Analiz", icon: BarChart3, id: "analytics" },
  { to: "/audit", label: "Denetim Günlüğü", icon: ScrollText, id: "audit" },
];
const MOBILE_NAV = NAV.slice(0, 4);
const ROLE = { owner: "Bina sahibi", manager: "İşletme yöneticisi" };

function SidebarContent({ onNavigate, onProfile, onLogout, user }) {
  return (
    <>
      <Link to="/home" className="brand" onClick={onNavigate} data-testid="brand-home-link"><span className="brand-mark"><Flame size={18}/></span><span>HAZMAT<span>CORE</span></span></Link>
      <div className="workspace-label">ANA MENÜ</div>
      <nav className="nav-list" data-testid="primary-navigation">
        {NAV.map(({ to, label, icon: Icon, id }) => (
          <NavLink key={id} to={to} onClick={onNavigate} className={({ isActive }) => `nav-item${isActive ? " active" : ""}`} data-testid={`nav-${id}`}>
            <Icon size={17}/>{label}
          </NavLink>
        ))}
      </nav>
      <div className="sidebar-bottom">
        <InstallAppButton/>
        <button className="user-chip" onClick={onProfile} data-testid="user-profile" title="Profil ve imza">
          <span className="avatar">{(user?.name || "U").slice(0, 2).toUpperCase()}</span>
          <span><strong>{user?.name || "Kullanıcı"}</strong><small>{ROLE[user?.role] || "Teknik danışman"} · Profil ve imza</small></span>
          <PenLine size={14}/>
        </button>
        <button className="user-chip logout-chip" onClick={onLogout} data-testid="logout-button">
          <span className="avatar"><LogOut size={14}/></span>
          <span><strong>Çıkış</strong><small>Oturumu kapat</small></span>
        </button>
      </div>
    </>
  );
}

function Breadcrumbs() {
  const crumbs = useCrumbState();
  return (
    <nav className="breadcrumbs app-crumbs" data-testid="breadcrumbs">
      <Link to="/home" data-testid="crumb-home">Ana Sayfa</Link>
      {crumbs.map((c, i) => (
        <span key={i} className="crumb">
          <ChevronRight size={12}/>
          {c.to && i < crumbs.length - 1 ? <Link to={c.to} data-testid={`crumb-${i}`}>{c.label}</Link> : <strong data-testid={`crumb-${i}`}>{c.label}</strong>}
        </span>
      ))}
    </nav>
  );
}

function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawer, setDrawer] = useState(false);
  const [profile, setProfile] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => { setDrawer(false); window.scrollTo(0, 0); }, [pathname]);
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(""), 2800); return () => clearTimeout(t); }, [toast]);

  const doLogout = async () => { await logout(); navigate("/login", { replace: true }); };
  const sidebarProps = { user, onNavigate: () => setDrawer(false), onProfile: () => setProfile(true), onLogout: doLogout };

  return (
    <div className="shell app-shell" data-testid="app-shell">
      <aside className="sidebar app-sidebar" data-testid="main-sidebar"><SidebarContent {...sidebarProps}/></aside>
      {drawer && (
        <div className="drawer-backdrop" onClick={() => setDrawer(false)} data-testid="mobile-drawer">
          <aside className="sidebar drawer" onClick={(e) => e.stopPropagation()}>
            <button className="icon-button drawer-close" onClick={() => setDrawer(false)} data-testid="mobile-drawer-close"><X size={16}/></button>
            <SidebarContent {...sidebarProps}/>
          </aside>
        </div>
      )}
      <main className="main-content">
        <header className="topbar app-topbar">
          <button className="icon-button mobile-menu-btn" onClick={() => setDrawer(true)} data-testid="mobile-menu-button"><Menu size={17}/></button>
          <Link to="/home" className="mobile-brand"><Flame size={18}/> HAZMATCORE</Link>
          <Breadcrumbs/>
        </header>
        <div className="app-page">{children}</div>
      </main>
      <nav className="bottom-nav" data-testid="bottom-nav">
        {MOBILE_NAV.map(({ to, label, icon: Icon, id }) => (
          <NavLink key={id} to={to} className={({ isActive }) => isActive ? "active" : ""} data-testid={`bottom-nav-${id}`}>
            <Icon size={19}/><span>{label.split(" ")[0]}</span>
          </NavLink>
        ))}
        <button onClick={() => setDrawer(true)} data-testid="bottom-nav-more"><Menu size={19}/><span>Menü</span></button>
      </nav>
      {profile && <ProfileModal onClose={() => setProfile(false)} onToast={setToast}/>}
      {toast && <div className="toast" data-testid="layout-toast">{toast}</div>}
    </div>
  );
}

export default function AppLayout({ children }) {
  return <CrumbProvider><Layout>{children}</Layout></CrumbProvider>;
}
